import React from "react";
import { Link, useMatches } from "react-router-dom";
import { BreadCrumbsContainer } from "./styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "../../assets/icons/home.svg";

import Crumb from "./Crumb";

export default function Breadcrumbs() {
  const matches = useMatches();

  const crumbs = matches
    .filter((match) => match.handle?.crumb)
    .map((match) => {
      const crumbFn = match.handle.crumb;
      const maybeLabels = crumbFn(match, match.data);

      if (maybeLabels instanceof Promise) {
        throw maybeLabels; // Let Suspense resolve it
      }

      return {
        path: match.pathname,
        labels: normalizeCrumbValues(maybeLabels),
      };
    });

  return (
    <BreadCrumbsContainer>
      <nav className="breadcrumb" aria-label="breadcrumbs">
        <ol>
          {/* Always start with Home */}
          <li>
            <Link to="/">
              <img src={HomeIcon} className="home-icon" alt="Home" />
            </Link>
          </li>
          <li className="crumb-spacer">
            <NavigateNextIcon
              sx={{
                fontSize: "14px",
                opacity: ".6",
                position: "relative",
                top: "2px",
              }}
            />
          </li>

          {crumbs.flatMap((crumb, routeIndex) =>
            crumb.labels.flatMap((value, labelIndex) => {
              const isLast =
                routeIndex === crumbs.length - 1 &&
                labelIndex === crumb.labels.length - 1;

              const key = `${crumb.path}-${labelIndex}`;

              const items = [
                <li key={key}>
                  {renderCrumb(value, crumb.path, !isLast)}
                </li>,
              ];

              // Add spacer li unless this is the very last element
              if (!isLast) {
                items.push(
                  <li key={`${key}-spacer`} className="crumb-spacer">
                    <NavigateNextIcon
                      sx={{
                        fontSize: "14px",
                        opacity: ".6",
                        position: "relative",
                        top: "2px",
                      }}
                    />
                  </li>
                );
              }

              return items;
            })
          )}
        </ol>
      </nav>
    </BreadCrumbsContainer>
  );
}

/**
 * Convert any CrumbValue into a consistent array format
 */
function normalizeCrumbValues(value) {
  if (Array.isArray(value)) return value;
  return [value];
}

/**
 * Render a single crumb (string, element, or link object)
 */
function renderCrumb(value, fallbackPath, isLinkAllowed) {
  // React element → pass through
  if (React.isValidElement(value)) {
    return isLinkAllowed ? <Link to={fallbackPath}>{value}</Link> : value;
  }

  // { label, to } object
  if (value && typeof value === "object" && "label" in value) {
    const label = value.label;
    const display = typeof label === "string" && label.length > 30
      ? <Crumb maxLength={30}>{label}</Crumb>
      : label;

    return isLinkAllowed
      ? <Link to={value.to}>{display}</Link>
      : display;
  }

  // Plain string – THIS IS YOUR CASE
  if (typeof value === "string") {
    const display = value.length > 30
      ? <Crumb maxLength={30}>{value}</Crumb>
      : value;

    return isLinkAllowed
      ? <Link to={fallbackPath}>{display}</Link>
      : <span>{display}</span>;
  }

  return null;
}
